import { Info, MoreVertical } from "lucide-react";
import * as React from "react";

import type { BedrockCatalogModel } from "@/shared/domain/orgs";
import {
  useBedrockCatalog,
  useDeleteBedrockModel,
  useUpdateBedrockModel,
  useVerifyBedrockModel,
} from "@/shared/domain/orgs/hooks";
import { missingPriceBlocker } from "@/shared/domain/orgs/orgModelConfig";
import {
  Badge,
  type BadgeVariant,
  ConfirmDialog,
  DropdownMenu,
  RobotoButton,
  Spinner,
  Table,
  Toggle,
  ToggleGroup,
  Tooltip,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import styles from "./AiModels.module.css";
import { BedrockModelDialog } from "./BedrockModelDialog";
import {
  BREAK_EVEN_RAU_PER_USD,
  catalogModelRequest,
  costRates,
  costSummary,
  deleteBlocker,
  describeRates,
  headlineMargin,
  marginText,
  offerBlocker,
  priceRates,
  priceSummary,
  rateMargin,
  TOKEN_RATE_KEYS,
  TOKEN_RATE_LABELS,
  usdText,
  type TokenRates,
} from "./bedrockModelForm";
import {
  MakeAvailablePanel,
  type OrgModelPanelMode,
} from "./MakeAvailablePanel";

/** A chat model's place in the Roboto default that new (and reset) orgs get. */
type NewOrgsOffer = "not_offered" | "offered" | "default";

const NEW_ORGS_LABELS: Record<NewOrgsOffer, string> = {
  not_offered: "Not offered",
  offered: "Offered",
  default: "Default",
};

const NEW_ORGS_FIELDS: Record<
  NewOrgsOffer,
  { in_roboto_default: boolean; is_default: boolean }
> = {
  not_offered: { in_roboto_default: false, is_default: false },
  offered: { in_roboto_default: true, is_default: false },
  default: { in_roboto_default: true, is_default: true },
};

function newOrgsOffer(model: BedrockCatalogModel): NewOrgsOffer {
  if (model.is_default) {
    return "default";
  }
  return model.in_roboto_default ? "offered" : "not_offered";
}

/**
 * Why a "New orgs" option cannot be picked for `model`, or `undefined`.
 * The default row keeps its place until another row takes the default (the
 * server moves it); a model {@link offerBlocker} refuses (no cost or price,
 * unverified, or no tool use) cannot join the offer, though one already in
 * it can always leave.
 */
function newOrgsBlocker(
  model: BedrockCatalogModel,
  option: NewOrgsOffer,
): string | undefined {
  const current = newOrgsOffer(model);
  if (option === current) {
    return undefined;
  }
  if (current === "default") {
    return "Choose another default first.";
  }
  if (option === "not_offered") {
    return undefined;
  }
  return offerBlocker(model);
}

/**
 * One row's "New orgs" control: Not offered / Offered / Default. Choosing
 * Default moves the default to this row.
 */
function NewOrgsControl({
  model,
  disabled,
  onChange,
}: {
  model: BedrockCatalogModel;
  disabled: boolean;
  onChange: (offer: NewOrgsOffer) => void;
}) {
  const current = newOrgsOffer(model);
  return (
    <ToggleGroup<NewOrgsOffer>
      size="sm"
      aria-label={`New orgs: ${model.label}`}
      value={[current]}
      onValueChange={([next]) => {
        // Pressing the current option again would clear the group; ignore it.
        if (next !== undefined && next !== current) {
          onChange(next);
        }
      }}
      disabled={disabled}
    >
      {(Object.keys(NEW_ORGS_LABELS) as NewOrgsOffer[]).map((option) => {
        const blocker = newOrgsBlocker(model, option);
        return (
          <Toggle<NewOrgsOffer>
            key={option}
            value={option}
            disabled={blocker !== undefined}
            title={blocker}
          >
            {NEW_ORGS_LABELS[option]}
          </Toggle>
        );
      })}
    </ToggleGroup>
  );
}

/** What the price column shows, said by its header's info button. */
const PRICE_COLUMN_HELP =
  "Customer price per million input / output tokens, with the AWS cost under it. " +
  "The badge is the lower of the input and output margins over break-even " +
  `(cost × ${BREAK_EVEN_RAU_PER_USD} RAU per $1). Hover a price for every rate, cache included.`;

/** The price column's heading, with an info button explaining it. */
function PriceColumnHead() {
  return (
    <span className={styles.priceHead}>
      Price / M tokens
      <Tooltip>
        <Tooltip.Trigger
          delay={150}
          render={
            <button
              type="button"
              className={styles.infoButton}
              aria-label={PRICE_COLUMN_HELP}
            />
          }
        >
          <Info size={14} aria-hidden="true" />
        </Tooltip.Trigger>
        <Tooltip.Content className={styles.priceTooltip}>
          {PRICE_COLUMN_HELP}
        </Tooltip.Content>
      </Tooltip>
    </span>
  );
}

/** A margin as a badge: green above break-even, grey at it, amber below. */
function MarginBadge({ margin }: { margin: number }) {
  const text = marginText(margin);
  let variant: BadgeVariant = "warning";
  if (text === "break-even") {
    variant = "secondary";
  } else if (margin > 0) {
    variant = "success";
  }
  return <Badge variant={variant}>{text}</Badge>;
}

/**
 * A priced chat model's rates: input / output price, the cost under it and
 * the headline margin, with every rate (cache included) in a tooltip and in
 * the trigger's accessible name.
 */
function PricedRates({
  model,
  cost,
  price,
}: {
  model: BedrockCatalogModel;
  cost: TokenRates;
  price: TokenRates;
}) {
  const margin = headlineMargin(cost, price);
  const summary = [
    priceSummary(price),
    costSummary(cost),
    ...(margin === null ? [] : [`margin ${marginText(margin)}`]),
  ].join(", ");
  return (
    <Tooltip>
      <Tooltip.Trigger
        delay={150}
        render={
          <button
            type="button"
            className={styles.priceTrigger}
            aria-label={`${summary}. ${describeRates(cost, price)}`}
          />
        }
      >
        <span className={styles.pricePrimary}>
          {priceSummary(price)}
          {margin !== null && <MarginBadge margin={margin} />}
        </span>
        <span className={styles.priceSecondary}>{costSummary(cost)}</span>
      </Tooltip.Trigger>
      <Tooltip.Content className={styles.priceTooltip}>
        <table
          className={styles.rateTable}
          aria-label={`${model.label} rates per million tokens`}
        >
          <caption>Per million tokens</caption>
          <thead>
            <tr>
              <th>
                <span className="sr-only">Rate</span>
              </th>
              <th>Cost</th>
              <th>Price</th>
              <th>Margin</th>
            </tr>
          </thead>
          <tbody>
            {TOKEN_RATE_KEYS.map((key) => {
              const rowMargin = rateMargin(cost[key], price[key]);
              return (
                <tr key={key}>
                  <th scope="row">{TOKEN_RATE_LABELS[key]}</th>
                  <td>{usdText(cost[key])}</td>
                  <td>{price[key]} RAU</td>
                  <td>{rowMargin === null ? "—" : marginText(rowMargin)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Tooltip.Content>
    </Tooltip>
  );
}

/**
 * One row's price cell. An entry with both cost and price shows its rates;
 * one missing either shows the half it has, a badge per missing half and a
 * button that opens the editor.
 */
function PriceCell({
  model,
  onSet,
}: {
  model: BedrockCatalogModel;
  onSet: () => void;
}) {
  const cost = costRates(model);
  const price = priceRates(model);
  if (cost !== null && price !== null) {
    return <PricedRates model={model} cost={cost} price={price} />;
  }
  const missing = [
    ...(cost === null ? ["cost"] : []),
    ...(price === null ? ["price"] : []),
  ];
  const setLabel = `Set ${missing.join(" and ")}`;
  return (
    <span className={styles.priceCell}>
      {price !== null && (
        <span className={styles.pricePrimary}>{priceSummary(price)}</span>
      )}
      {cost !== null && (
        <span className={styles.priceSecondary}>{costSummary(cost)}</span>
      )}
      <span className={styles.needsPrice}>
        {cost === null && <Badge variant="error">Needs a cost</Badge>}
        {price === null && <Badge variant="error">Needs a price</Badge>}
        <RobotoButton size="sm" variant="secondary" onClick={onSet}>
          {setLabel}
        </RobotoButton>
      </span>
    </span>
  );
}

/**
 * One action in a row's menu. A blocked action stays listed, disabled, with
 * the reason under its label (the item's accessible description), since a
 * `title` on a menu item shows only on a slow hover.
 */
function RowAction({
  blocked,
  variant,
  onClick,
  children,
}: {
  /** Why the action is unavailable, or `undefined` when it is available. */
  blocked?: string;
  variant?: "default" | "danger";
  onClick: () => void;
  children: React.ReactNode;
}) {
  const reasonId = React.useId();
  return (
    <DropdownMenu.Item
      disabled={blocked !== undefined}
      aria-describedby={blocked === undefined ? undefined : reasonId}
      variant={variant}
      onClick={onClick}
    >
      <span className={styles.rowAction}>
        {children}
        {blocked !== undefined && (
          // Hidden from the name; read as the item's description instead.
          <span id={reasonId} aria-hidden className={styles.rowActionReason}>
            {blocked}
          </span>
        )}
      </span>
    </DropdownMenu.Item>
  );
}

/**
 * The deployment's Bedrock model catalog, edited at runtime by Roboto
 * admins: which models exist, what AWS charges Roboto for them and what
 * customers are charged, whether they have been verified, which ones make
 * up the Roboto default that new and reset orgs get, and which of those is
 * the default.
 */
export function BedrockCatalog() {
  const catalogQuery = useBedrockCatalog();
  const updateMutation = useUpdateBedrockModel();
  const verifyMutation = useVerifyBedrockModel();
  const deleteMutation = useDeleteBedrockModel();

  // `null` = closed; `undefined` model = adding a new one.
  const [editing, setEditing] = React.useState<{
    model: BedrockCatalogModel | undefined;
  } | null>(null);
  const [deleting, setDeleting] = React.useState<BedrockCatalogModel | null>(
    null,
  );
  // The model whose org panel is open, and whether it adds or removes it.
  const [offering, setOffering] = React.useState<{
    model: BedrockCatalogModel;
    mode: OrgModelPanelMode;
  } | null>(null);

  const models = catalogQuery.data ?? [];
  const busy = updateMutation.isPending || deleteMutation.isPending;

  const actionError =
    updateMutation.error ?? verifyMutation.error ?? deleteMutation.error;

  return (
    <div className={styles.catalog}>
      <div className={styles.catalogHeader}>
        <p className={styles.note}>
          Models Roboto runs on Bedrock. Models in the Roboto default are what
          new orgs, and orgs reset to the default, are offered.
        </p>
        <RobotoButton
          size="sm"
          onClick={() => setEditing({ model: undefined })}
        >
          Add model
        </RobotoButton>
      </div>

      {actionError && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(actionError)}
        </p>
      )}
      {catalogQuery.error && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(catalogQuery.error)}
        </p>
      )}
      {catalogQuery.isLoading && <Spinner aria-label="Loading models" />}

      {models.length > 0 && (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>Model</Table.Head>
              <Table.Head>Bedrock model id</Table.Head>
              <Table.Head scope="col">
                <PriceColumnHead />
              </Table.Head>
              <Table.Head>New orgs</Table.Head>
              <Table.Head>Verified</Table.Head>
              <Table.Head>Used by</Table.Head>
              <Table.Head>
                <span className="sr-only">Actions</span>
              </Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {models.map((model) => {
              const blockedDelete = deleteBlocker(model);
              const blockedOffer = offerBlocker(model);
              const missingPrice = missingPriceBlocker(model);
              const verifying =
                verifyMutation.isPending &&
                verifyMutation.variables?.modelId === model.id;
              return (
                <Table.Row key={model.id}>
                  <Table.Cell>
                    <span className={styles.modelName}>
                      <span className={styles.orgName}>{model.label}</span>
                      {!model.capabilities.tool_use && (
                        <span title="Tool use is off, and every chat turn carries tools.">
                          <Badge variant="warning">
                            Can&apos;t be a chat model
                          </Badge>
                        </span>
                      )}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.modelIdCell}>{model.model_id}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <PriceCell
                      model={model}
                      onSet={() => setEditing({ model })}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <NewOrgsControl
                      model={model}
                      disabled={busy}
                      onChange={(offer) =>
                        updateMutation.mutate({
                          modelId: model.id,
                          request: catalogModelRequest(
                            model,
                            NEW_ORGS_FIELDS[offer],
                          ),
                        })
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {model.verified ? (
                      "Verified"
                    ) : (
                      <span className={styles.unverified}>
                        Unverified
                        {model.last_error !== null && (
                          <span className={styles.lastError}>
                            {model.last_error}
                          </span>
                        )}
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {model.used_by_org_count}{" "}
                    {model.used_by_org_count === 1 ? "org" : "orgs"}
                  </Table.Cell>
                  <Table.Cell>
                    <DropdownMenu>
                      <DropdownMenu.Trigger
                        render={
                          <RobotoButton
                            variant="ghost"
                            size="sm"
                            icon={<MoreVertical />}
                            loading={verifying}
                            aria-label={`Actions for ${model.label}`}
                          />
                        }
                      />
                      <DropdownMenu.Content
                        align="end"
                        className={styles.rowActionsMenu}
                      >
                        <RowAction
                          blocked={blockedOffer}
                          onClick={() => setOffering({ model, mode: "add" })}
                        >
                          Make available to orgs
                        </RowAction>
                        {/* Removal is never blocked: an unverified or
                            unpriced model is exactly the kind to pull. */}
                        <RowAction
                          onClick={() => setOffering({ model, mode: "remove" })}
                        >
                          Remove from orgs
                        </RowAction>
                        <RowAction
                          blocked={missingPrice}
                          onClick={() =>
                            verifyMutation.mutate({ modelId: model.id })
                          }
                        >
                          Verify
                        </RowAction>
                        <RowAction onClick={() => setEditing({ model })}>
                          Edit
                        </RowAction>
                        <DropdownMenu.Separator />
                        <RowAction
                          blocked={blockedDelete}
                          variant="danger"
                          onClick={() => setDeleting(model)}
                        >
                          Delete
                        </RowAction>
                      </DropdownMenu.Content>
                    </DropdownMenu>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}

      {offering !== null && (
        <MakeAvailablePanel
          // A fresh panel per model and mode: its selection and result
          // belong to it.
          key={`${offering.mode}:${offering.model.id}`}
          model={offering.model}
          mode={offering.mode}
          catalog={models}
          onClose={() => setOffering(null)}
        />
      )}

      <BedrockModelDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null);
          }
        }}
        model={editing?.model}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleting(null);
          }
        }}
        title={`Delete ${deleting?.label ?? "model"}?`}
        description="It is removed from the catalog. No org uses it, so no chat changes."
        confirmLabel="Delete"
        destructive
        pending={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(
              { modelId: deleting.id },
              { onSettled: () => setDeleting(null) },
            );
          }
        }}
      />
    </div>
  );
}
